import { useEffect, useMemo, useState } from "react";
import { Button, Card, Form } from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ArrowLeft, Eye } from "lucide-react";
import { BannerService } from "@services/banners/bannerService";
import type { Banner, BannerStatus } from "@services/banners/commands/cmdListBanners";
import type { BannerConflict } from "@services/banners/commands/cmdCreateBanner";
import Message from "@/components/common/Message";
import Loader from "@/components/common/loader/loader";
import showAlert from "src/shared/utils/generic.tsx";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { ET_ZONE, formatEt } from "@/shared/utils/date-utils/easternTime";
import { Paths } from "@/paths";
import { MARKETING_BANNERS, useFeatureFlag } from "@/shared/featureFlags/useFeatureFlag";
import type { BannerPreviewState } from "@/modules/dashboard/banner/bannerPreview";
import {
    MAX_LINK_TEXT, MAX_MESSAGE, counterText, emptyForm, findOverlap, fromBanner, fromPayload, publishLabel, toPatch, toPayload, validate, validateLive, windowSummary,
    type BannerEditorRouterState, type BannerFormErrors, type BannerFormField, type BannerFormValues,
} from "./bannerForm";
import BannerLivePreview from "./components/BannerLivePreview";
import OverlapAlert from "./components/OverlapAlert";
import "./banners.scss";

const bannerService = new BannerService();

const listPath = `${import.meta.env.BASE_URL}${Paths.BANNERS}`;

/** The server's invalid_banner field names, as form fields. */
const SERVER_FIELDS: Record<string, BannerFormField> = {
    message: "message",
    link_text: "linkText",
    link_url: "linkUrl",
    starts_at: "start",
    ends_at: "end",
};

type ApiError = Error & { code?: string; status?: number; body?: { details?: { conflict?: BannerConflict | null; field?: string } } };

function savedToast(b: Banner): string {
    if (b.status === "Draft") return "Draft saved.";
    if (b.status === "Live") return "Banner is live on the Partner Dashboard.";
    return `Banner scheduled for ${formatEt(b.starts_at)}.`;
}

/** Create (banners/new) and edit (banners/:bannerId/edit) a Banner (MPN-2265). */
export default function BannerEditorPage() {
    const { bannerId } = useParams<{ bannerId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    // MPN-2266's dashboard preview comes back with the unsaved draft; it wins over the fetch (D6).
    const routerDraft = (location.state as BannerEditorRouterState | null)?.bannerDraft;
    const isEdit = !!bannerId;
    const canPreview = useFeatureFlag(MARKETING_BANNERS);
    useDocumentTitle(isEdit ? "Edit Banner" : "New Banner");

    const [values, setValues] = useState<BannerFormValues>(() => (routerDraft ? fromPayload(routerDraft) : emptyForm()));
    const [errors, setErrors] = useState<BannerFormErrors>({});
    const [conflict, setConflict] = useState<BannerConflict | null>(null);
    const [loadedStatus, setLoadedStatus] = useState<BannerStatus | undefined>();
    const [loadedStart, setLoadedStart] = useState<string | null>(null);
    // The other Live and Scheduled banners, for the live overlap check while typing.
    const [others, setOthers] = useState<Banner[]>([]);
    const [loading, setLoading] = useState(isEdit);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!bannerId) return;
        let cancelled = false;
        bannerService.getBanner(bannerId)
            .then((b) => {
                if (cancelled) return;
                if (b.status === "Expired") {
                    setLoadError("Expired banners are read only.");
                    return;
                }
                setLoadedStatus(b.status);
                setLoadedStart(b.starts_at);
                if (!routerDraft) setValues(fromBanner(b));
            })
            .catch((e: ApiError) => {
                if (cancelled) return;
                if (e.status === 404) setLoadError("Banner not found.");
                else if (e.status === 403) setLoadError("You don't have access to Banners.");
                else setLoadError(e.message || "Failed to load the banner.");
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // routerDraft is read once, on arrival.
    }, [bannerId]);

    useEffect(() => {
        let cancelled = false;
        bannerService.listBanners()
            .then((list) => {
                if (!cancelled) setOthers(list.filter((b) => b.uuid !== bannerId && (b.status === "Live" || b.status === "Scheduled")));
            })
            // Without the list there is no live check; the server's 409 still refuses an overlap.
            .catch(() => {});
        return () => { cancelled = true; };
    }, [bannerId]);

    const liveConflict = useMemo(() => findOverlap(values, others), [values, others]);

    function change<K extends BannerFormField>(field: K, value: BannerFormValues[K]) {
        setValues((v) => ({ ...v, [field]: value }));
        setErrors((e) => ({ ...e, [field]: undefined }));
        setConflict(null);
    }

    async function save(asDraft: boolean) {
        const found = validate(values, asDraft);
        if (Object.values(found).some(Boolean)) {
            setErrors(found);
            return;
        }
        setSaving(true);
        try {
            const saved = bannerId
                ? await bannerService.updateBanner(bannerId, toPatch(values, asDraft, loadedStart))
                : await bannerService.createBanner(toPayload(values, asDraft));
            showAlert(savedToast(saved), "success", 3000);
            navigate(listPath);
        } catch (e) {
            const err = e as ApiError;
            const details = err.body?.details;
            const field = details?.field ? SERVER_FIELDS[details.field] : undefined;
            if (err.code === "banner_overlap" && details?.conflict) setConflict(details.conflict);
            else if (err.code === "invalid_banner" && field) setErrors((prev) => ({ ...prev, [field]: err.message }));
            else showAlert(err.message, "error");
        } finally {
            setSaving(false);
        }
    }

    /** Shows the unsaved banner on the Partner Dashboard (MPN-2266). Nothing is saved anywhere. */
    function previewOnDashboard() {
        if (!values.message.trim()) {
            setErrors((e) => ({ ...e, message: "Message is required." }));
            return;
        }
        const draft = toPayload(values, true);
        // The draft rides on this entry too, so the browser's Back restores the form.
        const editorState: BannerEditorRouterState = { bannerDraft: draft, bannerId };
        navigate(location.pathname, { replace: true, state: editorState });
        const previewState: BannerPreviewState = { bannerPreview: draft, bannerId, returnTo: location.pathname };
        navigate(`${import.meta.env.BASE_URL}${Paths.HOME}`, { state: previewState });
    }

    const title = isEdit ? "Edit Banner" : "New Banner";
    const now = DateTime.now();
    // Save-attempt errors clear as a field is edited; the live rules show while typing.
    const shown: BannerFormErrors = { ...errors, ...validateLive(values) };
    // A 409 from the last save wins until a field changes; otherwise the live check.
    const shownConflict = conflict ?? liveConflict;

    return (
        <div className="banners-page banner-editor">
            <Message />
            <div className="banners-page__header">
                <h1>{title}</h1>
                <Button variant="link" className="d-inline-flex align-items-center gap-1" onClick={() => navigate(listPath)}>
                    <ArrowLeft size={16} aria-hidden="true" /> Back to Banners
                </Button>
            </div>

            {loading ? (
                <Loader />
            ) : loadError ? (
                <div className="alert alert-danger" role="alert">
                    {loadError} <Link to={listPath}>Back to Banners</Link>
                </div>
            ) : (
                <>
                    <BannerLivePreview message={values.message} linkText={values.linkText} linkUrl={values.linkUrl} />

                    <Card className="banners-page__card">
                        <Card.Body>
                            <h2 className="banners-page__card-title">Banner Details</h2>
                            <Form noValidate onSubmit={(e) => e.preventDefault()}>
                                <Form.Group className="mb-3" controlId="banner-message">
                                    <Form.Label>Message *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g. New: Direct Mail is now available as a channel."
                                        maxLength={MAX_MESSAGE}
                                        value={values.message}
                                        isInvalid={!!shown.message}
                                        onChange={(e) => change("message", e.target.value)}
                                    />
                                    <Form.Control.Feedback type="invalid">{shown.message}</Form.Control.Feedback>
                                    <Form.Text muted>{counterText(values)}</Form.Text>
                                </Form.Group>

                                <div className="row">
                                    <Form.Group className="col-md-6 mb-3" controlId="banner-link-text">
                                        <Form.Label>Link Text</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="e.g. Learn more"
                                            maxLength={MAX_LINK_TEXT}
                                            value={values.linkText}
                                            isInvalid={!!shown.linkText}
                                            onChange={(e) => change("linkText", e.target.value)}
                                        />
                                        <Form.Control.Feedback type="invalid">{shown.linkText}</Form.Control.Feedback>
                                        <Form.Text muted>Optional. Fill in with Link URL.</Form.Text>
                                    </Form.Group>
                                    <Form.Group className="col-md-6 mb-3" controlId="banner-link-url">
                                        <Form.Label>Link URL</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="e.g. https://motimatic.com/direct-mail"
                                            value={values.linkUrl}
                                            isInvalid={!!shown.linkUrl}
                                            onChange={(e) => change("linkUrl", e.target.value)}
                                        />
                                        <Form.Control.Feedback type="invalid">{shown.linkUrl}</Form.Control.Feedback>
                                        <Form.Text muted>Optional. Must start with https://</Form.Text>
                                    </Form.Group>
                                </div>

                                {/* No app-level provider; the timezone prop makes the pickers edit ET whatever the browser's zone. */}
                                <LocalizationProvider dateAdapter={AdapterLuxon}>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <Form.Label htmlFor="banner-start">Start *</Form.Label>
                                            <DateTimePicker
                                                value={values.start}
                                                onChange={(v: DateTime | null) => change("start", v)}
                                                timezone={ET_ZONE}
                                                ampm
                                                slotProps={{
                                                    textField: { id: "banner-start", size: "small", fullWidth: true, error: !!shown.start, helperText: shown.start ?? "Eastern Time (ET). Required to schedule." },
                                                }}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <Form.Label htmlFor="banner-end">End</Form.Label>
                                            <DateTimePicker
                                                value={values.end}
                                                onChange={(v: DateTime | null) => change("end", v)}
                                                timezone={ET_ZONE}
                                                ampm
                                                slotProps={{
                                                    textField: { id: "banner-end", size: "small", fullWidth: true, error: !!shown.end, helperText: shown.end ?? "Eastern Time (ET). Leave blank to run until turned off." },
                                                    field: { clearable: true },
                                                }}
                                            />
                                        </div>
                                    </div>
                                </LocalizationProvider>

                                {shownConflict && <OverlapAlert conflict={shownConflict} />}

                                <div className="banner-editor__summary">
                                    <strong className="banner-editor__audience">Audience: All Partner Dashboard users</strong>
                                    {" | "}{windowSummary(values, now)}
                                </div>

                                <div className="banner-editor__footer">
                                    <div className="banner-editor__footer-start">
                                        {canPreview && (
                                            <Button
                                                variant="neutral"
                                                className="d-inline-flex align-items-center gap-1"
                                                disabled={saving}
                                                onClick={previewOnDashboard}
                                            >
                                                <Eye size={16} aria-hidden="true" /> Preview on Partner Dashboard
                                            </Button>
                                        )}
                                    </div>
                                    <Button variant="link" disabled={saving} onClick={() => navigate(listPath)}>Cancel</Button>
                                    {/* A Live banner can only be turned off, not sent back to Draft (MPN-2171 design). */}
                                    {loadedStatus !== "Live" && (
                                        <Button variant="neutral" disabled={saving} onClick={() => save(true)}>Save Draft</Button>
                                    )}
                                    <Button variant="primary" disabled={saving || !!liveConflict} onClick={() => save(false)}>
                                        {publishLabel(values, now, loadedStatus)}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </>
            )}
        </div>
    );
}
